import { useEffect, useMemo, useRef, useState } from "react";
import { keepPreviousData } from "@tanstack/react-query";
import type { FonteDaFoto } from "@foto/shared";
import { trpc } from "../../lib/trpc.js";
import type { FotoNaBandeja } from "./folhas.js";
import { filtroDeVivencia, vivenciaAoTrocarDeLocal } from "./filtro-de-vivencia.js";
import { paginaQueExiste, totalDePaginas } from "./paginas-da-bandeja.js";

/** Quantas fotos a bandeja soma a cada "Mostrar mais". */
const PASSO = 60;
/** O teto que o servidor aceita num pedido. */
const TETO = 300;
/** A pausa na digitação antes de consultar: uma consulta por letra seria ruído. */
const PAUSA_DA_BUSCA_MS = 300;

/**
 * As fotos publicadas que a Impressão e os Marcadores oferecem: a consulta, os
 * filtros (nome ou telefone, local e vivência — ou "Sem vivência", #100) e o "mostrar mais".
 *
 * Com `porPagina` (a Impressão, #103), a lista vem em páginas desse tamanho em vez de
 * crescer com o "mostrar mais"; trocar um filtro volta à primeira página.
 *
 * `vistas` é o mapa de **todas as fotos que já chegaram**, e só cresce. A folha
 * guarda o id das fotos que o operador colocou nos espaços, e a prévia precisa da
 * imagem delas mesmo quando um filtro novo as tira da lista.
 */
export function useFotosPublicadas(ids?: number[], opcoes: { porPagina?: number } = {}) {
  const { porPagina } = opcoes;
  const [busca, setBusca] = useState("");
  const [buscaAplicada, setBuscaAplicada] = useState("");
  const [institutionId, setInstitutionId] = useState("");
  // Um id de vivência, "" (todas) ou `SEM_VIVENCIA`.
  const [vivenciaId, setVivenciaId] = useState("");
  const [limite, setLimite] = useState(PASSO);
  const [pagina, setPagina] = useState(0);
  // A versão que as miniaturas mostram e que um toque escolhe: a peça com capa, a foto
  // que a IA gerou ou a original (só na Impressão).
  const [versao, setVersao] = useState<FonteDaFoto>("peca");

  useEffect(() => {
    const timer = setTimeout(() => {
      setBuscaAplicada((atual) => {
        const nova = busca.trim();
        if (nova !== atual) {
          setLimite(PASSO);
          setPagina(0);
        }
        return nova;
      });
    }, PAUSA_DA_BUSCA_MS);
    return () => clearTimeout(timer);
  }, [busca]);

  const consulta = trpc.impressao.fotos.useQuery(
    {
      ...(ids && ids.length > 0 ? { ids } : {}),
      ...(buscaAplicada ? { busca: buscaAplicada } : {}),
      ...(institutionId ? { institutionId } : {}),
      ...filtroDeVivencia(vivenciaId),
      ...(porPagina ? { limite: porPagina, offset: pagina * porPagina } : { limite }),
    },
    // Trocar o filtro mantém a lista antiga na tela até a nova chegar: sem isso a
    // grade piscaria em branco a cada letra.
    { placeholderData: keepPreviousData },
  );

  const acumuladas = useRef(new Map<number, FotoNaBandeja>());
  const vistas = useMemo(() => {
    // As pedidas por id vêm inteiras à parte na primeira página: a folha de partida as usa todas.
    for (const f of consulta.data?.pedidas ?? []) acumuladas.current.set(f.id, f);
    for (const f of consulta.data?.itens ?? []) acumuladas.current.set(f.id, f);
    return new Map(acumuladas.current);
  }, [consulta.data]);

  const itens: FotoNaBandeja[] = consulta.data?.itens ?? [];
  const total = consulta.data?.total ?? 0;
  const filtrando = busca.trim() !== "" || institutionId !== "" || vivenciaId !== "";
  const paginas = porPagina ? totalDePaginas(total, porPagina) : 1;

  // Uma página que ficou além do fim — fotos despublicadas em outra aba, por exemplo — volta para a última.
  const dados = consulta.data;
  useEffect(() => {
    if (!porPagina || !dados || consulta.isPlaceholderData) return;
    const existe = paginaQueExiste(pagina, dados.total, porPagina);
    if (existe !== pagina) setPagina(existe);
  }, [dados, consulta.isPlaceholderData, pagina, porPagina]);

  /** Um filtro novo é outra lista: volta ao começo dela. */
  function recomecar() {
    setLimite(PASSO);
    setPagina(0);
  }

  return {
    consulta,
    itens,
    total,
    vistas,
    filtrando,

    versao,
    escolherVersao: setVersao,
    /** Alguma foto da lista tem as duas versões: só então a chave faz sentido. */
    temVersaoDaIa: itens.some((f) => f.foto),
    /** Alguma foto da lista tem a original à parte (as feitas com IA). */
    temVersaoOriginal: itens.some((f) => f.original),

    busca,
    institutionId,
    vivenciaId,
    buscar: setBusca,
    /** Trocar o local zera a vivência, que só existe dentro de um local; "Sem vivência" fica. */
    escolherLocal: (id: string) => {
      setInstitutionId(id);
      setVivenciaId(vivenciaAoTrocarDeLocal);
      recomecar();
    },
    escolherVivencia: (id: string) => {
      setVivenciaId(id);
      recomecar();
    },
    limparFiltros: () => {
      setBusca("");
      setBuscaAplicada("");
      setInstitutionId("");
      setVivenciaId("");
      recomecar();
    },

    /** Há mais fotos que as mostradas, e o servidor ainda aceita pedir mais (sem páginas). */
    temMais: !porPagina && itens.length < total && limite < TETO,
    mostrarMais: () => setLimite((l) => Math.min(TETO, l + PASSO)),

    /** As páginas, quando há `porPagina`; sem ele, uma página só. */
    porPagina,
    pagina,
    paginas,
    irParaPagina: (n: number) => setPagina(Math.max(0, Math.min(n, paginas - 1))),
  };
}

export type FotosPublicadas = ReturnType<typeof useFotosPublicadas>;
